import { publishEvent } from 'serviceWorker/broadcasts/publishers';
import type { ChannelContract, ChannelContractShape } from 'shared/channels';
import { DomainMessagesTypes, isPullFailedMessage, Message, Services } from 'shared/domain/messages/message';
import { isNoProjectAccessStatus } from './httpError';

/**
 * Tells the UI that a pull failed, so listeners waiting for `hasAll` can stop
 * their spinner instead of waiting forever / for a fallback timeout.
 *
 * The error is serialized to a plain object: with the broadcast-channel
 * fallbacks (idb/localstorage) the message goes through JSON and an Error
 * instance would turn into `{}`.
 */

/** A contract whose listeners stop waiting for its collection when its pull fails. */
export type PullingContract = ChannelContract<
  ChannelContractShape & { events: { [DomainMessagesTypes.pullFailed]: unknown } }
>;

export interface SerializedPullError {
  name: string;
  message: string;
  status?: number;
  entityName: Services;
  projectId?: string;
}

/** A `pullFailed` event whose pull the API refused: the user has no access to the project. */
export function isNoProjectAccessPullFailure(message: Message): boolean {
  const { error } = message as Message & { error?: Partial<SerializedPullError> };
  return isPullFailedMessage(message) && isNoProjectAccessStatus(error?.status);
}

interface ErrorLike {
  name?: unknown;
  message?: unknown;
  status?: unknown;
  responseBody?: { error?: { message?: unknown } };
}

export function serializePullError(
  error: unknown,
  entityName: Services
): SerializedPullError {
  if (error && typeof error === 'object') {
    const e = error as ErrorLike;

    // HttpError's message is "Something went wrong"; the actual reason
    // (e.g. "does not have access to project ...") is in responseBody.
    const serverMessage =
      typeof e.responseBody?.error?.message === 'string'
        ? e.responseBody.error.message
        : undefined;
    const ownMessage =
      typeof e.message === 'string' && e.message !== ''
        ? e.message
        : undefined;

    return {
      name: typeof e.name === 'string' ? e.name : 'Error',
      message: serverMessage ?? ownMessage ?? `Pull of ${entityName} failed`,
      ...(typeof e.status === 'number' ? { status: e.status } : {}),
      entityName,
    };
  }

  return {
    name: 'Error',
    message:
      error === undefined || error === null
        ? `Pull of ${entityName} failed`
        : String(error),
    entityName,
  };
}

/**
 * Broadcasts `pullFailed` on the entity's channel, with the serialized error and
 * no `data` (listeners treat `data` as a successful payload).
 * Never throws – the pull's catch must still clean up its state.
 * Returns false when the broadcast failed.
 */
export async function broadcastPullFailed({
  channelContract,
  entityName,
  error,
  projectId,
}: {
  channelContract: PullingContract;
  entityName: Services;
  error: unknown;
  projectId?: string;
}): Promise<boolean> {
  try {
    publishEvent(channelContract, DomainMessagesTypes.pullFailed, undefined, {
      error: {
        ...serializePullError(error, entityName),
        ...(projectId !== undefined ? { projectId } : {}),
      },
    });
    return true;
  } catch {
    return false;
  }
}
